import React, { useEffect, useState } from "react";
import { Button, Form, Input, Space, Spin, Typography } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import message from "@/utils/globalMessage";
import { api } from "@/redux/api";
import ResponsiveSelect from "@components/ResponsiveSelect";
import { SERVICE_REQUEST_PRIORITIES } from "@/containers/service-request/constants";
import "@/containers/service-request/styles.css";

export default function TenantServiceRequestCreatePage() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const [facilities, setFacilities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const listPath = "/tennant/service-request";

  useEffect(() => {
    Promise.all([api.getTenantFacilities(), api.getTenantServiceCategories()])
      .then(([facilityResponse, categoryResponse]) => {
        const facilityRows = facilityResponse.data || [];
        const categoryPayload = categoryResponse.data;
        const categoryRows = Array.isArray(categoryPayload)
          ? categoryPayload
          : categoryPayload?.results || [];
        setFacilities(facilityRows);
        setCategories(categoryRows);

        const facilityId = facilityRows.length === 1 ? facilityRows[0].id : undefined;
        form.setFieldsValue({ facility: facilityId, priority: "Low" });
      })
      .catch(() => message.error("Failed to load request options"))
      .finally(() => setLoading(false));
  }, [form]);

  const submit = async (values) => {
    setSubmitting(true);
    try {
      const response = await api.createTenantServiceRequest(values);
      message.success("Service request created successfully");
      navigate(`/tennant/service-request-detail/${response.data.id}`, { replace: true });
    } catch (error) {
      message.error("Failed to create service request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="service-request-create-page">
      {isPC && <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(listPath)} title="Back" />
        <Typography.Title level={4} style={{ margin: 0 }}>Create Service Request</Typography.Title>
      </div>}

      <Spin spinning={loading}>
        <Form className="service-request-create-form" form={form} layout="vertical" onFinish={submit} autoComplete="off">
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: "Please enter a title" }]}
          >
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item
            label="Facility"
            name="facility"
            rules={[{ required: true, message: "Please select a facility" }]}
          >
            <ResponsiveSelect placeholder="Select a facility" popupTitle="Select Facility" showSearch>
              {facilities.map((item) => (
                <ResponsiveSelect.Option key={item.id} value={item.id}>{item.name}</ResponsiveSelect.Option>
              ))}
            </ResponsiveSelect>
          </Form.Item>
          <Form.Item
            label="Suite / Area"
            name="suite_area"
            rules={[{ required: true, message: "Please enter Suite / Area" }]}
          >
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <ResponsiveSelect placeholder="Select a category" popupTitle="Select Category" showSearch>
              {categories.map((item) => (
                <ResponsiveSelect.Option key={item.id} value={item.id}>{item.name}</ResponsiveSelect.Option>
              ))}
            </ResponsiveSelect>
          </Form.Item>
          <Form.Item label="Priority" name="priority" rules={[{ required: true }]}>
            <ResponsiveSelect placeholder="Select a priority" popupTitle="Select Priority">
              {SERVICE_REQUEST_PRIORITIES.map((value) => (
                <ResponsiveSelect.Option key={value} value={value}>{value}</ResponsiveSelect.Option>
              ))}
            </ResponsiveSelect>
          </Form.Item>
          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Please enter a description" }]}
          >
            <Input.TextArea rows={6} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={submitting}>
              Create Request
            </Button>
            <Button onClick={() => navigate(listPath)}>Cancel</Button>
          </Space>
        </Form>
      </Spin>
    </div>
  );
}
